import React, { useState, useEffect } from 'react';
import { Button } from '../components/Button';
import { SelectField, TextField } from '../components/Fields';
import { AuthLayout } from '../components/AuthLayout';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders, getAccessToken, getRefreshToken, handleAuthError, requireAccessToken } from '../utils/auth';

function NewNumberForm() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [newTradingLoading, setNewTradingLoading] = useState(false);
	const [newTradingMessage, setNewTradingMessage] = useState('');
	const [agreed, setAgreed] = useState(false);
	
	// Get action and data from URL parameters
	const action = searchParams.get('action') || 'cont';
	const checkNumber = searchParams.get('check_number') || '';
	const vcode = searchParams.get('vcode') || '';
	
	const [formData, setFormData] = useState({
		old: '',
		new: '',
		pass: '',
		ipass: '',
		server: '',
		vcode: ''
	});
	
	// Load data from URL params
	useEffect(() => {
		setFormData(prev => ({
			...prev,
			old: checkNumber,
			vcode: vcode
		}));
	}, [checkNumber, vcode]);
	
	// Redirect if invalid action
	useEffect(() => {
		if (action !== 'cont' && action !== 'add_sca' && action !== 'add_aso') {
			navigate('/selfservice/continuation');
		}
	}, [action, navigate]);

	const handleSubmit = (e) => {
		e.preventDefault();
		if (!requireAccessToken()) return;
		setNewTradingLoading(true);
		
		// Validation
		if (formData.ipass === '') {
			setNewTradingMessage('Investor password is required');
			setNewTradingLoading(false);
			return;
		}
		if (formData.pass === '') {
			setNewTradingMessage('Password is required');
			setNewTradingLoading(false);
			return;
		}
		if (formData.new === '') {
			setNewTradingMessage('New trading account number is required');
			setNewTradingLoading(false);
			return;
		}
		if (formData.server === '') {
			setNewTradingMessage('Server is required');
			setNewTradingLoading(false);
			return;
		}
		if ((action === 'add_aso' || action === 'add_sca') && formData.vcode === '') {
			setNewTradingMessage('Verification code is required');
			setNewTradingLoading(false);
			return;
		}

		// Submit form
		axios.post(
			'https://maindashbe-june-b18731a0e161.herokuapp.com/selfservice/add-new-deriv/',
			{
				old_number: formData.old,
				new_number: formData.new,
				password: formData.pass,
				investor_password: formData.ipass,
				service_type: action === 'add_sca' ? 'sca' : action === 'add_aso' ? 'aso' : 'continuation',
				verification_code: formData.vcode,
				server: formData.server
			},
			{
				headers: getAuthHeaders()
			}
		)
			.then(res => {
				if (res.data.message) {
					setNewTradingMessage(res.data.message);
				}
				setNewTradingLoading(false);
				if (res.data.message !== 'Account type not supported' && 
					res.data.message !== 'Old account already has a new account' && 
					res.data.message !== 'Invalid verification code' && 
					res.data.message !== 'You have an active Aso account already.') {
					window.location.href = process.env.REACT_APP_PUBLIC_ENVIRONMENT === 'PRODUCTION' 
						? `https://dashboard.ft9ja.com/dashboards?token=${getAccessToken()}&refresh_token=${getRefreshToken()}` 
						: `https://ft9ja-completion-v1-dashboard.netlify.app/dashboards?token=${getAccessToken()}&refresh_token=${getRefreshToken()}`;
				}
			})
			.catch(err => {
				if (handleAuthError(err)) return;
				if (err.response?.status === 500) {
					setNewTradingMessage('Server error, please contact support');
				}
				setNewTradingLoading(false);
			});
	};

	const getTitle = () => {
		// This page is ONLY for continuation flow - never show synthetic signal titles
		if (action === 'add_sca') return 'Add Second Chance Account';
		if (action === 'add_aso') return 'Add Aso Account';
		// Default to continuation for 'cont' or any other case
		return 'Account Continuation';
	};

	const getOldAccountLabel = () => {
		if (action === 'add_sca') return 'First Zuma Trading Account Number';
		if (action === 'add_aso') return 'Qualified Zuma/SCA Trading Account Number';
		return 'Old Trading Account Number';
	};

	// Safety check: Don't render if somehow we have an invalid action
	if (action !== 'cont' && action !== 'add_sca' && action !== 'add_aso') {
		return null;
	}

	return (
		<AuthLayout>
			<form onSubmit={handleSubmit}>
				<div className="grid grid-cols-1 gap-6">
					<h1 className="text-center font-medium text-[1.3rem]">{getTitle()}</h1>
					<TextField
						label={getOldAccountLabel()}
						id="old_trading_account_number"
						name="old_trading_account_number"
						type="name"
						value={formData.old || ''}
						readOnly
						autoComplete="old_trading_account_number"
						required
					/>
					<TextField
						label="New Trading Account number"
						id="new_trading_account_number"
						name="new_trading_account_number"
						type="name"
						value={formData.new || ''}
						onChange={e => setFormData(prev => ({ ...prev, new: e.target.value.replace(/[^0-9]/g, '') }))}
						autoComplete="given-name"
						required
					/>
					<TextField
						className="col-span-full"
						label="Trading Password"
						id="trading_password"
						name="trading_password"
						type="password"
						value={formData.pass || ''}
						onChange={e => setFormData(prev => ({ ...prev, pass: e.target.value }))}
						autoComplete="trading_password"
						required
					/>
					<TextField
						className="col-span-full"
						label="Investor Password"
						id="investor_password"
						name="investor_password"
						type="password"
						value={formData.ipass || ''}
						onChange={e => setFormData(prev => ({ ...prev, ipass: e.target.value }))}
						autoComplete="investor_password"
						required
					/>

					{(action === 'add_sca' || action === 'add_aso') && (
						<TextField
							className="col-span-full"
							label="Verification Code"
							id="verification_code"
							name="verification_code"
							type="text"
							value={formData.vcode || ''}
							readOnly
							autoComplete="verification_code"
							required
						/>
					)}

					<SelectField
						className="col-span-full"
						label="Server"
						id="server"
						name="server"
						value={formData.server || ''}
						onChange={e => setFormData(prev => ({ ...prev, server: e.target.value }))}
					>
						<option value="" disabled>Choose Server</option>
						<option value="Deriv-Demo">Deriv-Demo</option>
						<option value="ForexTimeFXTM-Demo01">ForexTimeFXTM-Demo01</option>
						<option value="ForexTimeFXTM-Demo02">ForexTimeFXTM-Demo02</option>
						<option value="ICMarketsSC-Demo">ICMarketsSC-Demo</option>
					</SelectField>
					<TextField
						className="col-span-full"
						label="Platform"
						id="platform"
						name="platform"
						type="text"
						value="MT5"
						readOnly
						autoComplete="platform"
						required
					/>
				</div>
				<p className="text-center text-red-500 mb-5">{newTradingMessage}</p>
				<div>
					<p>Tampering with the account details (login, password, leverage, topping up the balance) without written approval from FT9ja violates the rule.</p>
					<div className="flex items-center gap-2 mt-5">
						<input type="checkbox" onChange={() => setAgreed(!agreed)} checked={agreed} />
						<p>I have read and agreed with the rules and terms</p>
					</div>
				</div>
				{agreed && (
					<Button type="submit" color="cyan" className="mt-8 w-full duration-200 hover:bg-gray-800 focus:bg-[#28a745] focus:text-white/80">
						{newTradingLoading ? 'Please Wait...' : 'Submit'}
					</Button>
				)}
			</form>
		</AuthLayout>
	);
}

export default NewNumberForm;

